import React from "react";
import * as Sentry from "@sentry/browser";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Boundary around the restake/unstake/withdraw forms. A render-time throw inside
 * these views (e.g. a value that slips past validation into parseUnits) is caught
 * here and degraded to an inline retry instead of breaking the whole page —
 * defense-in-depth alongside the input/URL validation that prevents such values.
 */
class RestakeErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    });
  }

  handleReset = () => this.setState({ hasError: false });

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex w-full flex-col items-center gap-3 py-10 text-center">
          <p className="text-text-primary text-base font-medium">
            Something went wrong loading this form.
          </p>
          <p className="text-sm text-gray-500">
            Please check your input and try again.
          </p>
          <button
            type="button"
            onClick={this.handleReset}
            className="mt-2 rounded-lg border border-gray-300 px-4 py-2 text-sm text-text-primary"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RestakeErrorBoundary;
